import { Tag, type TagIntent } from '@omnipresentgroup/design-system';
import { useGetContractorInvoice } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type ContractorInvoice,
  ContractorInvoiceStateEnum,
} from '@omnipresentgroup/types';

export type StatusOverride = Partial<
  Record<
    ContractorInvoiceStateEnum,
    {
      intent?: TagIntent;
      text?: string;
    }
  >
>;

// eslint-disable-next-line no-magic-numbers
const isOverdueInThePast = (overdue: number) => overdue > 0;

const getIntent = (
  state: ContractorInvoiceStateEnum,
  overdueBy: number,
  statusOverride?: StatusOverride,
): { intent: TagIntent; text: string } => {
  switch (state) {
    case ContractorInvoiceStateEnum.PAID: {
      const status = statusOverride?.[ContractorInvoiceStateEnum.PAID];
      return {
        intent: status?.intent ?? 'success',
        text: status?.text ?? toSentenceCase(state.replace(/_/g, ' ')),
      };
    }
    case ContractorInvoiceStateEnum.OPEN: {
      const status = statusOverride?.[ContractorInvoiceStateEnum.OPEN];
      return {
        intent:
          (status?.intent ?? isOverdueInThePast(overdueBy))
            ? 'warning'
            : 'info',
        text: status?.text ?? 'Delivered',
      };
    }
    case ContractorInvoiceStateEnum.IN_PROGRESS: {
      const status = statusOverride?.[ContractorInvoiceStateEnum.IN_PROGRESS];
      return {
        intent: status?.intent ?? 'info',
        text: status?.text ?? 'Delivered',
      };
    }
    case ContractorInvoiceStateEnum.REJECTED: {
      const status = statusOverride?.[ContractorInvoiceStateEnum.REJECTED];
      return {
        intent: status?.intent ?? 'default',
        text: status?.text ?? toSentenceCase(state),
      };
    }
    case ContractorInvoiceStateEnum.CANCELLED: {
      const status = statusOverride?.[ContractorInvoiceStateEnum.CANCELLED];
      return {
        intent: status?.intent ?? 'default',
        text: status?.text ?? toSentenceCase(state),
      };
    }
    default: {
      return {
        intent: 'default',
        text: '',
      };
    }
  }
};

type Props = {
  statusOverride?: StatusOverride;
} & (
  | { invoiceId: string; invoice?: never }
  | { invoice: ContractorInvoice; invoiceId?: never }
);

export const ContractorInvoiceStatus = ({
  invoiceId,
  invoice,
  statusOverride,
}: Props) => {
  const { data: fetchedInvoice } = useGetContractorInvoice({
    id: invoiceId,
    enabled: Boolean(invoiceId) && !invoice,
  });

  const { state, id, overdueBy } = invoice || fetchedInvoice || {};
  const overdue = Number(overdueBy);
  const statusConfig = state && getIntent(state, overdue, statusOverride);

  const showOverdue =
    invoice?.state === ContractorInvoiceStateEnum.OPEN &&
    isOverdueInThePast(overdue);

  return invoice ? (
    <Tag id={`status-${id}`} intent={statusConfig?.intent}>
      {showOverdue ? `${overdue} days overdue` : statusConfig?.text}
    </Tag>
  ) : null;
};
